import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from './Button';
import { useAuth } from '../context/AuthContext';

interface NavbarProps {
    onLoginClick: () => void;
    onRegisterClick: () => void;
    onMenuClick?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onLoginClick, onRegisterClick, onMenuClick }) => {
    const { user, isAuthenticated, logout } = useAuth();
    const navigate = useNavigate();
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const userFirstNameInitial = (user?.firstName || user?.email || 'U').charAt(0).toUpperCase();
    const userLastNameInitial = (user?.lastName || user?.email || '').charAt(0).toUpperCase();

    const renderDirectActionButton = () => {
        if (!user) return null;

        if (user.role === 'user-organizer') {
            return (
                <Button
                    variant="ghost"
                    size="md"
                    className="hidden md:flex mr-1 text-[var(--st-text-secondary)] hover:text-[var(--st-accent-primary)]"
                    onClick={() => navigate('/organizer/dashboard')}
                    icon={
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
                    }
                >
                    Dashboard
                </Button>
            );
        }

        if (user.role === 'user-client-buyer') {
            return (
                <Button
                    variant="ghost"
                    size="sm"
                    className="hidden sm:flex mr-1 text-[var(--st-text-secondary)] hover:text-[var(--st-accent-primary)]"
                    onClick={() => navigate('/tickets')}
                    icon={
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" /></svg>
                    }
                >
                    Mis Tickets
                </Button>
            );
        }

        return null;
    };

    return (
        <header className="sticky top-0 z-50 st-glass">
            <div className="st-container">
                <div className="flex items-center justify-between h-16 sm:h-20">

                    <div className="flex items-center gap-3">
                        {isAuthenticated && (
                            <button
                                onClick={onMenuClick}
                                className="md:hidden p-2 -ml-2 text-[var(--st-text-secondary)] hover:bg-[var(--st-bg-tertiary)] rounded-lg transition-colors"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" /></svg>
                            </button>
                        )}

                        {/* LOGO */}
                        <Link to="/" className="flex items-center gap-2 cursor-pointer no-underline">
                            <svg width="40" height="32" viewBox="0 0 50 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 sm:w-auto sm:h-auto">
                                <path d="M5 10 L22 10 L22 5 L27 5 L27 10 L45 10 L45 30 L27 30 L27 35 L22 35 L22 30 L5 30 Z" fill="#6366f1"/>
                                <circle cx="24.5" cy="10" r="5" fill="#f8fafc"/>
                                <circle cx="24.5" cy="30" r="5" fill="#f8fafc"/>
                                <line x1="12" y1="17" x2="18" y2="17" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                                <line x1="12" y1="20" x2="20" y2="20" stroke="white" strokeWidth="2" strokeLinecap="round" opacity="0.6"/>
                                <line x1="12" y1="23" x2="16" y2="23" stroke="white" strokeWidth="2" strokeLinecap="round" opacity="0.4"/>
                                <path d="M35 13 L30 20 L35 20 L32 27 L40 18 L35 18 Z" fill="#f59e0b"/>
                            </svg>
                            <span className="font-display text-lg sm:text-xl font-bold text-[var(--st-text-primary)] hidden sm:block">SuperTicket</span>
                        </Link>
                    </div>

                    {/* BARRA DE BÚSQUEDA (Desktop) */}
                    <div className="hidden md:flex flex-1 max-w-xl mx-8">
                        <div className="relative w-full">
                            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--st-text-muted)]">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            <input type="text" placeholder="Buscar eventos..." className="st-input st-input-with-icon w-full" />
                        </div>
                    </div>

                    {/* ACCIONES DEL USUARIO */}
                    <div className="flex items-center gap-2 sm:gap-4">
                        {isAuthenticated ? (
                            <div className="flex items-center gap-2">

                                {renderDirectActionButton()}

                                <div className="relative">
                                    <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="flex items-center gap-3 focus:outline-none">
                                        <div className="text-right hidden sm:block">
                                            <p className="text-sm font-semibold text-[var(--st-text-primary)] leading-none">{user?.firstName}</p>
                                        </div>
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--st-accent-primary)] to-[var(--st-accent-secondary)] text-white flex items-center justify-center font-bold text-lg shadow-md ring-2 ring-white hover:ring-[var(--st-accent-primary-light)] transition-all">
                                            {userFirstNameInitial}{userLastNameInitial}
                                        </div>
                                    </button>

                                    {/* MENU DESPLEGABLE */}
                                    {isMenuOpen && (
                                        <div className="absolute right-0 mt-3 w-48 bg-white rounded-xl shadow-xl border border-[var(--st-border)] py-2 animate-fade-in z-50">
                                            {user?.role === 'user-organizer' && (
                                                <button onClick={() => { setIsMenuOpen(false); navigate('/organizer/dashboard'); }} className="w-full text-left px-4 py-2 text-sm text-[var(--st-text-secondary)] hover:bg-[var(--st-bg-tertiary)] hover:text-[var(--st-text-primary)] transition-colors">Dashboard</button>
                                            )}
                                            {user?.role === 'user-client-buyer' && (
                                                <button onClick={() => { setIsMenuOpen(false); navigate('/tickets'); }} className="w-full text-left px-4 py-2 text-sm text-[var(--st-text-secondary)] hover:bg-[var(--st-bg-tertiary)] hover:text-[var(--st-text-primary)] transition-colors">Mis Tickets</button>
                                            )}
                                            <button onClick={() => { setIsMenuOpen(false); navigate('/account'); }} className="w-full text-left px-4 py-2 text-sm text-[var(--st-text-secondary)] hover:bg-[var(--st-bg-tertiary)] hover:text-[var(--st-text-primary)] transition-colors">Mi cuenta</button>
                                            <div className="h-px bg-[var(--st-border)] my-1"></div>
                                            <button onClick={logout} className="w-full text-left px-4 py-2 text-sm text-[var(--st-error)] hover:bg-[var(--st-error-light)] transition-colors font-medium">Cerrar Sesión</button>
                                        </div>
                                    )}
                                    {isMenuOpen && (<div className="fixed inset-0 z-40" onClick={() => setIsMenuOpen(false)}></div>)}
                                </div>
                            </div>
                        ) : (
                            <>
                                <Button variant="ghost" size="md" className="st-btn st-btn-ghost" onClick={onLoginClick}>Iniciar Sesión</Button>
                                <Button variant="primary" className="st-btn st-btn-primary" onClick={onRegisterClick}>Registrarse</Button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Navbar;